import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import axiosInstance from "../../api/axios";
import DotsLoader from "../../components/icons/DotsLoader";
import CloseModalIcon from "../../components/icons/profile/closeModal";
import SavedIcon from "../../components/icons/searchIcons/savedIcon";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import LettersLoader from "../../components/LettersLoader";
import LoginModal from "../../components/shared/LoginModal/LoginModal";
import { ROUTES } from "../../constants/routes";
import useLikeClick from "../../hooks/useLikeClick";
import useFavouriteClick from "../../hooks/useFavouriteClick";
import useWindowSize from "../../hooks/useWindowSize";
import MovieCard from "../search/MoviesList/MovieCard";
import VerticalMovieCard from "../search/MoviesList/VerticalMovieCard";
import { Movie } from "../search/MoviesList/MoviesList";
import {
  useGetPersonDetailsQuery,
  useGetPersonMoviesQuery,
  useGetPersonRelatedQuery,
} from "../../store/api/filmAPI";
import { setLoginModal } from "../../store/slices/modals.slice";
import { useAppSelector } from "../../store/store";
import { languageHandler } from "../../utils/helpers";
import actorPulseIcon from "../../components/icons/actor/ctor.svg";
import styles from "./ActorPage.module.scss";
import LinkGuide from "../../components/shared/LinkGuide/LinkGuide";

type PersonDetail = {
  id: number;
  name: string | null;
  biography: string | null;
  birthday: string | null;
  deathday: string | null;
  place_of_birth: string | null;
  profile_path: string | null;
  known_for_department: string | null;
  also_known_as: string[] | null;
  in_favorite: boolean;
  movies_count: number;
};

type RelatedPerson = {
  id: number;
  name: string | null;
  profile_path: string | null;
};

const getImageSrc = (path?: string | null) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;

  return `${process.env.REACT_APP_IMG_BASE_URL}${path}`;
};

const getPersonName = (name?: string | null, alsoKnownAs?: string[] | null) => {
  return name || alsoKnownAs?.find(Boolean) || "Unknown actor";
};

const getAge = (birthday: string, deathday?: string | null) => {
  const startDate = new Date(birthday);
  const endDate = deathday ? new Date(deathday) : new Date();
  let age = endDate.getFullYear() - startDate.getFullYear();
  const monthDelta = endDate.getMonth() - startDate.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && endDate.getDate() < startDate.getDate())) {
    age -= 1;
  }

  return age;
};

const getBirthdayLabel = (birthday: string | null, deathday: string | null, language: string, yearsLabel: string) => {
  if (!birthday) return "";

  const date = new Date(birthday);
  if (Number.isNaN(date.getTime())) return "";

  const formattedDate = new Intl.DateTimeFormat(language, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);

  return `${formattedDate} (${getAge(birthday, deathday)} ${yearsLabel})`;
};

const getDescriptionParagraphs = (description?: string | null) => {
  if (!description?.trim()) return [];

  return description
    .split(/\r?\n\s*\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const getErrorStatus = (error: unknown) => {
  if (error && typeof error === "object" && "status" in error) {
    return (error as { status?: number | string }).status;
  }

  return undefined;
};

const ActorPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { windowSize } = useWindowSize();
  const isMobile = windowSize === "mobile";
  const language = languageHandler(i18n.language) as string;
  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();
  const { loginModal } = useAppSelector((state: any) => state.modals);
  const paginationAnchorRef = useRef(null);

  const [moviesPage, setMoviesPage] = useState(1);
  const [moviesPrevPage, setMoviesPrevPage] = useState(0);
  const [moviesList, setMoviesList] = useState<Movie[]>([]);
  const [actorInFavorite, setActorInFavorite] = useState(false);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  const {
    data: personData,
    isLoading: personLoading,
    error: personError,
    refetch: refetchPerson,
  } = useGetPersonDetailsQuery(
    {
      id,
      queries: { language },
    },
    { skip: !id },
  );

  const { data: moviesData, isFetching: moviesFetching } = useGetPersonMoviesQuery(
    {
      id,
      queries: { language, page: moviesPage },
    },
    { skip: !id },
  );

  const { data: relatedData } = useGetPersonRelatedQuery(
    {
      id,
      queries: { language },
    },
    { skip: !id },
  );

  const person = personData as PersonDetail | undefined;
  const relatedActors = Array.isArray(relatedData) ? (relatedData as RelatedPerson[]) : [];
  const profileImage = getImageSrc(person?.profile_path);
  const actorName = getPersonName(person?.name, person?.also_known_as);
  const birthdayLabel = getBirthdayLabel(person?.birthday || null, person?.deathday || null, i18n.language, t("yearsOld"));
  const moviesTotal = moviesData?.total_results || moviesData?.count || person?.movies_count || moviesList.length;
  const isLastMoviesPage = moviesData?.page ? moviesData.page === moviesData.total_pages : true;
  const descriptionParagraphs = getDescriptionParagraphs(person?.biography);

  useEffect(() => {
    if (getErrorStatus(personError) === 404) {
      navigate(ROUTES.NOT_FOUND_PAGE, { replace: true });
    }
  }, [personError, navigate]);

  useEffect(() => {
    setMoviesPage(1);
    setMoviesPrevPage(0);
    setMoviesList([]);
    window.scrollTo(0, 0);
  }, [id, language]);

  useEffect(() => {
    if (person) {
      setActorInFavorite(Boolean(person.in_favorite));
    }
  }, [person]);

  useEffect(() => {
    if (!descriptionModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDescriptionModalOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [descriptionModalOpen]);

  useEffect(() => {
    if (!moviesFetching && moviesData?.page > moviesPrevPage) {
      setMoviesList((prevList) => {
        const nextList = moviesData.page === 1 ? moviesData.results : [...prevList, ...moviesData.results];
        return nextList.filter(
          (movie: Movie, index: number, self: Movie[]) => index === self.findIndex((item) => item.id === movie.id),
        );
      });
      setMoviesPrevPage(moviesData.page);
    }
  }, [moviesFetching, moviesData, moviesPrevPage]);

  useEffect(() => {
    if (moviesFetching || isLastMoviesPage) return;

    const elem = paginationAnchorRef.current;
    const handleIntersection = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && moviesList.length) {
          setMoviesPage((currentPage) => currentPage + 1);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0.1,
    });

    if (elem) {
      observer.observe(elem);
    }

    return () => {
      if (elem) {
        observer.unobserve(elem);
      }
    };
  }, [moviesFetching, isLastMoviesPage, moviesList.length]);

  const updateMovieField = (movieId: number, key: string, type: string) => {
    setMoviesList((prevList) =>
      prevList.map((movie: any) =>
        movie.id === movieId
          ? {
              ...movie,
              [key]: type === "increment" ? (movie[key] || 0) + 1 : Math.max((movie[key] || 0) - 1, 0),
            }
          : movie,
      ),
    );
  };

  const toggleActorFavorite = async () => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      dispatch(setLoginModal(true));
      return;
    }

    try {
      if (actorInFavorite) {
        await axiosInstance.delete(`/person/favorites/${id}/`);
      } else {
        await axiosInstance.post("/person/favorites/", { person: Number(id) });
      }
      setActorInFavorite((currentValue) => !currentValue);
      refetchPerson();
    } catch (e) {
      console.log(e);
    }
  };

  if (personLoading) {
    return (
      <div className={styles.loaderPage}>
        <LettersLoader />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header source="modal" className={styles.headerWrapper} backButtonClassName={styles.backButton} />
      <div className={styles.backgroundGlow} style={profileImage ? { backgroundImage: `url(${profileImage})` } : {}} />
      <main className={styles.content}>
        <section className={styles.heroSection}>
          <div className={styles.cover}>
            {profileImage ? <img src={profileImage} alt={actorName} /> : <div className={styles.coverPlaceholder}>{actorName[0]}</div>}
          </div>
          <div className={styles.heroInfo}>
            {(birthdayLabel || person?.known_for_department) && (
              <div className={styles.metaBadge}>
                <img className={styles.pulseIcon} src={actorPulseIcon} alt="" aria-hidden="true" />
                <span>{birthdayLabel || person?.known_for_department}</span>
              </div>
            )}
            <div className={styles.nameBlock}>
              <h1>{actorName}</h1>
              {person?.place_of_birth && <p>{person.place_of_birth}</p>}
            </div>
          </div>
          <div className={styles.actions}>
            <button
              className={styles.providerButton}
              type="button"
              aria-label="actor description"
              onClick={() => setDescriptionModalOpen(true)}
            >
              T
            </button>
            <button className={styles.favoriteButton} type="button" onClick={toggleActorFavorite}>
              <SavedIcon className={styles.favoriteIcon} inFavourites={actorInFavorite} />
              <span>{actorInFavorite ? t("inFavorites") : t("toFavorites")}</span>
            </button>
          </div>
        </section>

        {!!relatedActors.length && (
          <section className={styles.relatedRail} aria-label={t("actors")}>
            {relatedActors.slice(0, 6).map((actor) => {
              const actorImage = getImageSrc(actor.profile_path);
              const relatedActorName = getPersonName(actor.name);

              return (
                <button
                  key={actor.id}
                  className={styles.relatedCard}
                  type="button"
                  onClick={() => navigate(`${ROUTES.ACTORS}/${actor.id}`)}
                >
                  {actorImage ? <img src={actorImage} alt={relatedActorName} /> : <span>{relatedActorName[0]}</span>}
                  <p>{relatedActorName}</p>
                </button>
              );
            })}
          </section>
        )}

        <section className={styles.moviesSection}>
          <div className={styles.sectionTitle}>
            <h2>{t("movies")}</h2>
            <span>{moviesTotal}</span>
          </div>
          {moviesFetching && !moviesList.length ? (
            <div className={styles.loaderWrapper}>
              <DotsLoader />
            </div>
          ) : (
            <div className={styles.moviesGrid}>
              {moviesList.map((movie) => {
                const cardProps = {
                  item: movie,
                  likeHandler,
                  isFav: Boolean(favoritesList?.data?.results?.includes(movie.id)),
                  liked: Boolean(likesList?.data?.results?.includes(movie.id)),
                  favouritesHandler,
                  updateVoteLocal: updateMovieField,
                };

                return isMobile ? (
                  <MovieCard key={movie.id} isMobile={true} {...cardProps} />
                ) : (
                  <VerticalMovieCard key={movie.id} isMobile={false} {...cardProps} />
                );
              })}
            </div>
          )}
          {!isLastMoviesPage && (
            <div className={styles.loaderWrapper} ref={paginationAnchorRef}>
              <DotsLoader />
            </div>
          )}
        </section>
      </main>
      <Footer source={""} />
      <LinkGuide/>
      {descriptionModalOpen && (
        <div className={styles.descriptionOverlay} role="presentation" onClick={() => setDescriptionModalOpen(false)}>
          <section
            className={styles.descriptionModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="actor-description-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.descriptionTitle}>
              <h2 id="actor-description-title">{actorName}</h2>
            </div>
            <div className={styles.descriptionContent}>
              {descriptionParagraphs.length ? (
                descriptionParagraphs.map((paragraph, index) => <p key={`${actorName}-${index}`}>{paragraph}</p>)
              ) : (
                <p>{person?.known_for_department || actorName}</p>
              )}
            </div>
            <button
              className={styles.descriptionClose}
              type="button"
              aria-label="close actor description"
              onClick={() => setDescriptionModalOpen(false)}
            >
              <CloseModalIcon />
            </button>
          </section>
        </div>
      )}
      {loginModal && <LoginModal />}
    </div>
  );
};

export default ActorPage;
